import React from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import {
  AlertTriangle,
  ArrowRight,
  Download,
  FileCheck2,
  FileText,
  Home,
  LoaderCircle,
  RefreshCw,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import "./RenovationProjectRenovationPlanStep.css";
import { useToast } from "../../../components/feedback/ToastProvider";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const STEP_DRAFT_KEY = "RENOVATION_PLAN";

function findStep(steps = []) {
  return (
    steps.find((step) => step.step_code === "RENOVATION_PLAN") || {
      step_code: "RENOVATION_PLAN",
      step_order: 3,
      status: "NOT_STARTED",
      is_optional: true,
      visible_to: "HB_OSS",
      editable_by: "HB_OSS",
    }
  );
}

function statusClass(status) {
  return String(status || "NOT_STARTED")
    .toLowerCase()
    .replaceAll("_", "-");
}

function formatDateTime(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatFileSize(bytes) {
  const value = Number(bytes);

  if (!Number.isFinite(value) || value <= 0) {
    return "";
  }

  if (value < 1024) {
    return `${value} B`;
  }

  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }

  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function ownerLabel(role, t) {
  if (role === "HB") {
    return t("actors.homebuyer", "Homebuyer");
  }

  if (role === "OSS") {
    return t("actors.oss", "One-Stop Shop");
  }

  return t("actors.unknown", "Project participant");
}

function validatePdf(file, t) {
  if (!(file instanceof File)) {
    return t("errors.fileRequired", "Please select a PDF file.");
  }

  const filename = String(file.name || "").toLowerCase();

  if (!filename.endsWith(".pdf")) {
    return t("errors.pdfOnly", "Only PDF files are allowed.");
  }

  const contentType = String(file.type || "").toLowerCase();

  if (
    contentType &&
    ![
      "application/pdf",
      "application/x-pdf",
      "application/octet-stream",
    ].includes(contentType)
  ) {
    return t("errors.pdfOnly", "Only PDF files are allowed.");
  }

  return "";
}

export default function RenovationProjectRenovationPlanStep() {
  const { t } = useTranslation("renovation-project-renovation-plan");
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { showToast } = useToast();

  const {
    project,
    steps,
    basePath,
    viewerRole,

    refreshProject,
    registerStepSaveHandler,
    setStepDirty,
    isStepDirty,
    isStepSaving,
    getStepDraft,
    setStepDraft,
    clearStepDraft,
  } = useOutletContext();

  const step = findStep(steps);

  const [documentInfo, setDocumentInfo] = React.useState(null);
  const [documentFile, setDocumentFile] = React.useState(null);

  const [viewer, setViewer] = React.useState({
    role: viewerRole || "",
    can_upload: false,
    can_replace: false,
    can_delete: false,
    document_owner_role: null,
  });

  const [stepStatus, setStepStatus] = React.useState(
    step.status || "NOT_STARTED",
  );

  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState("");

  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isLocalSaving, setIsLocalSaving] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);

  const fileInputRef = React.useRef(null);
  const localDirtyRef = React.useRef(false);

  const canSelectFile = Boolean(
    !documentInfo ? viewer.can_upload : viewer.can_replace,
  );

  const isCurrentOwner =
    Boolean(documentInfo) &&
    Boolean(viewer.document_owner_role) &&
    viewer.document_owner_role === viewer.role;

  /* =========================================================
     DRAFT HELPERS
     ========================================================= */

  const persistDraft = React.useCallback(
    (file) => {
      if (typeof setStepDraft !== "function") {
        return;
      }

      if (file instanceof File) {
        setStepDraft(STEP_DRAFT_KEY, {
          documentFile: file,
        });
      } else {
        clearStepDraft?.(STEP_DRAFT_KEY);
      }
    },
    [clearStepDraft, setStepDraft],
  );

  /* =========================================================
     LOAD STEP 3
     ========================================================= */

  const loadRenovationPlan = React.useCallback(
    async ({ restoreDraft = true, showLoading = true } = {}) => {
      if (!project?.id) {
        return;
      }

      if (showLoading) {
        setLoading(true);
      }

      setLoadError("");

      try {
        const token = await getAccessTokenSilently();

        const response = await fetch(
          `${API_BASE}/api/projects/${project.id}/renovation-plan/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data?.detail ||
              t("errors.loadFailed", "Failed to load the renovation plan."),
          );
        }

        const nextDocument = data?.document || null;
        const nextViewer = {
          role: data?.viewer?.role || viewerRole || "",
          can_upload: Boolean(data?.viewer?.can_upload),
          can_replace: Boolean(data?.viewer?.can_replace),
          can_delete: Boolean(data?.viewer?.can_delete),
          document_owner_role:
            data?.viewer?.document_owner_role ||
            nextDocument?.uploaded_by_role ||
            null,
        };

        setDocumentInfo(nextDocument);
        setViewer(nextViewer);
        setStepStatus(data?.step_status || step.status || "NOT_STARTED");

        if (restoreDraft && typeof getStepDraft === "function") {
          const draft = getStepDraft(STEP_DRAFT_KEY);
          const draftFile = draft?.documentFile;

          const draftAllowed = nextDocument
            ? nextViewer.can_replace
            : nextViewer.can_upload;

          if (draftFile instanceof File && draftAllowed) {
            setDocumentFile(draftFile);
            localDirtyRef.current = true;
            setStepDirty?.(true);
          } else if (draftFile) {
            clearStepDraft?.(STEP_DRAFT_KEY);
          }
        }
      } catch (requestError) {
        setLoadError(
          requestError?.message ||
            t("errors.loadFailed", "Failed to load the renovation plan."),
        );
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [
      clearStepDraft,
      getAccessTokenSilently,
      getStepDraft,
      project?.id,
      setStepDirty,
      step.status,
      t,
      viewerRole,
    ],
  );

  React.useEffect(() => {
    loadRenovationPlan({
      restoreDraft: true,
      showLoading: true,
    });
  }, [loadRenovationPlan]);

  /* =========================================================
     FILE SELECTION
     ========================================================= */

  const selectFile = React.useCallback(
    (file) => {
      if (!canSelectFile) {
        return;
      }

      const validationError = validatePdf(file, t);

      if (validationError) {
        showToast({
          type: "error",
          title: t("toasts.fileErrorTitle", "Invalid file"),
          message: validationError,
        });
        return;
      }

      setDocumentFile(file);

      localDirtyRef.current = true;

      persistDraft(file);
      setStepDirty?.(true);
    },
    [canSelectFile, persistDraft, setStepDirty, showToast, t],
  );

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (file) {
      selectFile(file);
    }

    /*
     * Reset the native input so selecting the same file again
     * still produces a change event.
     */
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();

    if (!canSelectFile) {
      return;
    }

    const file = event.dataTransfer.files?.[0];

    if (file) {
      selectFile(file);
    }
  };

  const clearPendingFile = () => {
    setDocumentFile(null);
    localDirtyRef.current = false;

    persistDraft(null);
    setStepDirty?.(false);
  };

  /* =========================================================
     SAVE / UPLOAD / REPLACE
     ========================================================= */

  const saveRenovationPlan = React.useCallback(
    async ({ notify = true } = {}) => {
      if (!project?.id) {
        throw new Error(
          t("errors.projectUnavailable", "Project is not available."),
        );
      }

      if (!(documentFile instanceof File)) {
        localDirtyRef.current = false;
        setStepDirty?.(false);
        clearStepDraft?.(STEP_DRAFT_KEY);

        return true;
      }

      const validationError = validatePdf(documentFile, t);

      if (validationError) {
        throw new Error(validationError);
      }

      const allowed = documentInfo ? viewer.can_replace : viewer.can_upload;

      if (!allowed) {
        throw new Error(
          documentInfo
            ? t(
                "errors.notOwner",
                "The current renovation plan was uploaded by the other project participant. Delete it before uploading a new one.",
              )
            : t(
                "errors.uploadNotAllowed",
                "You cannot upload a renovation plan.",
              ),
        );
      }

      setIsLocalSaving(true);

      try {
        const token = await getAccessTokenSilently();

        const formData = new FormData();
        formData.append("file", documentFile);

        const response = await fetch(
          `${API_BASE}/api/projects/${project.id}/renovation-plan/document/`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data?.detail ||
              data?.file ||
              t("errors.saveFailed", "Failed to save the renovation plan."),
          );
        }

        setDocumentInfo(data?.document || null);

        setViewer((current) => ({
          ...current,
          ...(data?.viewer || {}),
        }));

        setStepStatus(data?.step_status || "IN_PROGRESS");
        setDocumentFile(null);

        localDirtyRef.current = false;

        clearStepDraft?.(STEP_DRAFT_KEY);
        setStepDirty?.(false);

        if (notify) {
          showToast({
            type: "success",
            title: data?.was_replacement
              ? t("toasts.replacedTitle", "Renovation plan updated")
              : t("toasts.uploadedTitle", "Renovation plan uploaded"),
            message: data?.was_replacement
              ? t(
                  "messages.replaced",
                  "The renovation plan was updated successfully.",
                )
              : t(
                  "messages.uploaded",
                  "The renovation plan was uploaded successfully.",
                ),
          });
        }

        return true;
      } catch (requestError) {
        const message =
          requestError?.message ||
          t("errors.saveFailed", "Failed to save the renovation plan.");

        if (notify) {
          showToast({
            type: "error",
            title: t("toasts.saveErrorTitle", "Could not save renovation plan"),
            message,
          });
        }

        throw new Error(message);
      } finally {
        setIsLocalSaving(false);
      }
    },
    [
      clearStepDraft,
      documentFile,
      documentInfo,
      getAccessTokenSilently,
      project?.id,
      setStepDirty,
      showToast,
      t,
      viewer.can_replace,
      viewer.can_upload,
    ],
  );

  React.useEffect(() => {
    if (!registerStepSaveHandler || !["HB", "OSS"].includes(viewerRole)) {
      return undefined;
    }

    return registerStepSaveHandler(() =>
      saveRenovationPlan({
        notify: false,
      }),
    );
  }, [registerStepSaveHandler, saveRenovationPlan, viewerRole]);

  /* =========================================================
     DOWNLOAD
     ========================================================= */

  const downloadDocument = async () => {
    if (!project?.id || !documentInfo?.id || isDownloading) {
      return;
    }

    setIsDownloading(true);

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/projects/${project.id}/documents/${documentInfo.id}/file/?disposition=attachment`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        throw new Error(
          data?.detail ||
            t(
              "errors.downloadFailed",
              "Failed to download the renovation plan.",
            ),
        );
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download =
        documentInfo.filename ||
        documentInfo.original_filename ||
        "renovation-plan.pdf";

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(objectUrl);
    } catch (requestError) {
      showToast({
        type: "error",
        title: t("toasts.downloadErrorTitle", "Download failed"),
        message:
          requestError?.message ||
          t("errors.downloadFailed", "Failed to download the renovation plan."),
      });
    } finally {
      setIsDownloading(false);
    }
  };

  /* =========================================================
     DELETE
     ========================================================= */

  const deleteDocument = async () => {
    if (!project?.id || !documentInfo?.id || isDeleting) {
      return;
    }

    setIsDeleting(true);

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/projects/${project.id}/renovation-plan/document/delete/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            t("errors.deleteFailed", "Failed to delete the renovation plan."),
        );
      }

      setDocumentInfo(null);
      setDocumentFile(null);

      setViewer((current) => ({
        ...current,
        ...(data?.viewer || {}),
        document_owner_role: null,
      }));

      setStepStatus(data?.step_status || "NOT_STARTED");

      localDirtyRef.current = false;

      clearStepDraft?.(STEP_DRAFT_KEY);
      setStepDirty?.(false);

      setDeleteModalOpen(false);

      showToast({
        type: "success",
        title: t("toasts.deletedTitle", "Renovation plan deleted"),
        message: t(
          "messages.deleted",
          "The renovation plan was deleted. Either project participant can now upload a new file.",
        ),
      });

      await refreshProject?.();
    } catch (requestError) {
      showToast({
        type: "error",
        title: t("toasts.deleteErrorTitle", "Could not delete renovation plan"),
        message:
          requestError?.message ||
          t("errors.deleteFailed", "Failed to delete the renovation plan."),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  /* =========================================================
     SAVE AND CONTINUE
     ========================================================= */

  const goNext = async () => {
    if (documentFile instanceof File || localDirtyRef.current) {
      try {
        const saved = await saveRenovationPlan({
          notify: true,
        });

        if (!saved) {
          return;
        }

        await refreshProject?.();
      } catch {
        return;
      }
    }

    navigate(`${basePath}/${project.id}/energy-performance-certificate`);
  };

  /* =========================================================
     LOADING / LOAD ERROR
     ========================================================= */

  if (loading) {
    return (
      <div className="renovation-step-loader">
        <LoaderCircle size={30} className="renovation-step-loader__spinner" />

        <span>{t("loading.renovationPlan", "Loading renovation plan...")}</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="renovation-step-load-error">
        <FileText size={26} />

        <strong>
          {t("errors.loadTitle", "Renovation plan could not be loaded")}
        </strong>

        <span>{loadError}</span>

        <button
          type="button"
          onClick={() =>
            loadRenovationPlan({
              restoreDraft: true,
              showLoading: true,
            })
          }
        >
          {t("buttons.tryAgain", "Try again")}
        </button>
      </div>
    );
  }

  const currentOwnerLabel = ownerLabel(
    documentInfo?.uploaded_by_role || viewer.document_owner_role,
    t,
  );

  const pendingIsReplacement = Boolean(documentInfo && documentFile);

  return (
    <>
      <article className="renovation-plan-step">
        {/* ===================================================
            HEADER
            =================================================== */}

        <header className="renovation-plan-step__header">
          <div className="renovation-plan-step__heading">
            <span className="renovation-plan-step__eyebrow">
              <Home size={15} />

              {t("renovationPlan.eyebrow", "Step 3")}
            </span>

            <div className="renovation-plan-step__title-row">
              <h1>{t("renovationPlan.title", "Renovation Plan")}</h1>

              <span className="renovation-plan-step__optional">
                {t("renovationPlan.optional", "Optional")}
              </span>
            </div>

            <p>
              {t(
                "renovationPlan.subtitle",
                "Manage the formal renovation plan and share the document with the other project participant.",
              )}
            </p>
          </div>

          <div className="renovation-plan-step__status">
            <span className="renovation-plan-step__stage-count">
              {t("renovationPlan.stage", "Stage {{current}} of {{total}}", {
                current: step.step_order || 3,
                total: 8,
              })}
            </span>

            <span
              className={
                `renovation-plan-step__status-pill ` +
                `renovation-plan-step__status-pill--${statusClass(stepStatus)}`
              }
            >
              {t(
                `stepStatuses.${stepStatus}`,
                String(stepStatus).replaceAll("_", " ").toLowerCase(),
              )}
            </span>
          </div>
        </header>

        {/* ===================================================
            DOCUMENT CARD
            =================================================== */}

        <section className="renovation-plan-card">
          <div className="renovation-plan-card__heading">
            <div>
              <FileText size={20} />

              <div>
                <h2>
                  {t("renovationPlan.cardTitle", "Technical renovation plan")}
                </h2>

                <p>
                  {t(
                    "renovationPlan.cardDescription",
                    "Upload the formal renovation plan as a PDF. Both the Homebuyer and the One-Stop Shop can provide the document.",
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* =================================================
              SAVED DOCUMENT
              ================================================= */}

          {documentInfo ? (
            <div className="renovation-plan-document">
              <div className="renovation-plan-document__icon">
                <FileCheck2 size={24} />
              </div>

              <div className="renovation-plan-document__content">
                <strong title={documentInfo.filename || ""}>
                  {documentInfo.filename ||
                    t("renovationPlan.documentFallback", "Renovation plan")}
                </strong>

                <div className="renovation-plan-document__meta">
                  <span>
                    {t("renovationPlan.uploadedBy", "Uploaded by {{actor}}", {
                      actor: currentOwnerLabel,
                    })}
                  </span>

                  {documentInfo.created_at ? (
                    <span>{formatDateTime(documentInfo.created_at)}</span>
                  ) : null}

                  {formatFileSize(documentInfo.size_bytes) ? (
                    <span>{formatFileSize(documentInfo.size_bytes)}</span>
                  ) : null}
                </div>

                {!isCurrentOwner ? (
                  <p className="renovation-plan-document__ownership-note">
                    {t(
                      "renovationPlan.otherOwnerNote",
                      "This file was uploaded by the other project participant. Delete it first if you want to upload a different renovation plan.",
                    )}
                  </p>
                ) : (
                  <p className="renovation-plan-document__ownership-note">
                    {t(
                      "renovationPlan.ownerNote",
                      "You are responsible for updates to this file. Select a new PDF below to replace it.",
                    )}
                  </p>
                )}
              </div>

              <div className="renovation-plan-document__actions">
                <button
                  type="button"
                  className="renovation-plan-action-btn"
                  onClick={downloadDocument}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <LoaderCircle
                      size={16}
                      className="renovation-plan-spinner"
                    />
                  ) : (
                    <Download size={16} />
                  )}

                  {isDownloading
                    ? t("buttons.downloading", "Downloading...")
                    : t("buttons.download", "Download")}
                </button>

                {viewer.can_delete ? (
                  <button
                    type="button"
                    className="renovation-plan-action-btn renovation-plan-action-btn--danger"
                    onClick={() => setDeleteModalOpen(true)}
                    disabled={isDeleting}
                  >
                    <Trash2 size={16} />

                    {t("buttons.delete", "Delete")}
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="renovation-plan-empty">
              <FileText size={23} />

              <div>
                <strong>
                  {t(
                    "renovationPlan.noDocumentTitle",
                    "No renovation plan uploaded yet",
                  )}
                </strong>

                <span>
                  {t(
                    "renovationPlan.noDocumentText",
                    "Either the Homebuyer or the One-Stop Shop can upload the first document.",
                  )}
                </span>
              </div>
            </div>
          )}

          {/* =================================================
              UPLOAD / REPLACEMENT
              ================================================= */}

          {canSelectFile ? (
            <div className="renovation-plan-upload">
              <span className="renovation-plan-upload__label">
                {documentInfo
                  ? t(
                      "renovationPlan.replaceLabel",
                      "Replace renovation plan document",
                    )
                  : t(
                      "renovationPlan.uploadLabel",
                      "Upload renovation plan document",
                    )}
              </span>

              {!documentFile ? (
                <button
                  type="button"
                  className="renovation-plan-dropzone"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={handleDrop}
                >
                  <UploadCloud size={21} />

                  <span>
                    <strong>
                      {t("renovationPlan.dropStrong", "Drag & drop a PDF here")}
                    </strong>{" "}
                    {t("renovationPlan.dropOr", "or")}{" "}
                    <u>{t("renovationPlan.browse", "browse")}</u>
                  </span>
                </button>
              ) : (
                <div className="renovation-plan-pending">
                  <div className="renovation-plan-pending__icon">
                    <UploadCloud size={21} />
                  </div>

                  <div className="renovation-plan-pending__content">
                    <span>
                      {pendingIsReplacement
                        ? t(
                            "renovationPlan.replacementSelected",
                            "Replacement selected",
                          )
                        : t("renovationPlan.fileSelected", "File selected")}
                    </span>

                    <strong title={documentFile.name}>
                      {documentFile.name}
                    </strong>

                    <small>{formatFileSize(documentFile.size)}</small>
                  </div>

                  <button
                    type="button"
                    className="renovation-plan-pending__remove"
                    onClick={clearPendingFile}
                    aria-label={t(
                      "buttons.removeSelectedFile",
                      "Remove selected file",
                    )}
                  >
                    <X size={17} />
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                hidden
                onChange={handleFileChange}
              />

              <p className="renovation-plan-upload__hint">
                {documentInfo
                  ? t(
                      "renovationPlan.replaceHint",
                      "Only the participant who uploaded the current document can replace it directly.",
                    )
                  : t(
                      "renovationPlan.uploadHint",
                      "PDF only. The participant who uploads the document becomes responsible for future replacements.",
                    )}
              </p>
            </div>
          ) : null}

          {documentInfo && !viewer.can_replace ? (
            <div className="renovation-plan-owner-lock">
              <RefreshCw size={17} />

              <span>
                {t(
                  "renovationPlan.replaceLocked",
                  "Direct replacement is available only to {{actor}}. They may delete the current file and then upload a new one.",
                  {
                    actor: currentOwnerLabel,
                  },
                )}
              </span>
            </div>
          ) : null}
        </section>

        {/* ===================================================
            CONTINUE
            =================================================== */}

        <button
          type="button"
          className="renovation-plan-next"
          onClick={goNext}
          disabled={isLocalSaving || isStepSaving || isDeleting}
        >
          {isLocalSaving || isStepSaving ? (
            <LoaderCircle size={18} className="renovation-plan-spinner" />
          ) : null}

          <span>
            {documentFile || isStepDirty
              ? t(
                  "buttons.saveAndContinue",
                  "Save and continue to Project / mid-project EPC",
                )
              : t("buttons.continue", "Continue to Project / mid-project EPC")}
          </span>

          <ArrowRight size={18} />
        </button>
      </article>

      {/* =====================================================
          DELETE CONFIRMATION
          ===================================================== */}

      {deleteModalOpen
        ? createPortal(
            <div
              className="renovation-plan-modal-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget && !isDeleting) {
                  setDeleteModalOpen(false);
                }
              }}
            >
              <div
                className="renovation-plan-delete-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="renovation-plan-delete-title"
              >
                <div className="renovation-plan-delete-modal__icon">
                  <AlertTriangle size={24} />
                </div>

                <h2 id="renovation-plan-delete-title">
                  {t("deleteModal.title", "Delete renovation plan?")}
                </h2>

                <p>
                  {t(
                    "deleteModal.message",
                    "This document will be removed from the renovation project. After deletion, either the Homebuyer or the One-Stop Shop can upload a new renovation plan.",
                  )}
                </p>

                {documentInfo?.filename ? (
                  <div className="renovation-plan-delete-modal__file">
                    <FileText size={16} />
                    <span>{documentInfo.filename}</span>
                  </div>
                ) : null}

                <div className="renovation-plan-delete-modal__actions">
                  <button
                    type="button"
                    className="renovation-plan-modal-btn"
                    onClick={() => setDeleteModalOpen(false)}
                    disabled={isDeleting}
                  >
                    {t("buttons.cancel", "Cancel")}
                  </button>

                  <button
                    type="button"
                    className="renovation-plan-modal-btn renovation-plan-modal-btn--danger"
                    onClick={deleteDocument}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <LoaderCircle
                        size={16}
                        className="renovation-plan-spinner"
                      />
                    ) : (
                      <Trash2 size={16} />
                    )}

                    {isDeleting
                      ? t("buttons.deleting", "Deleting...")
                      : t("buttons.deleteDocument", "Delete document")}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
